import * as THREE from 'three';
import type { Weather } from '../sim/Weather';

interface RainOptions {
  area?: number;
  maxDrops?: number;
}

/**
 * Cheap rain particle system. A single Points cloud follows the camera on the
 * XZ plane so drops never visibly drift away. Actual rain intensity drives
 * `drawRange.count`, so a calm sky costs zero drawn vertices while a full
 * storm fills `maxDrops`.
 */
export class Rain {
  readonly group: THREE.Group;

  private readonly weather: Weather;
  private readonly area: number;
  private readonly maxDrops: number;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsMaterial;
  private readonly points: THREE.Points;

  private readonly positions: Float32Array;
  private readonly velocities: Float32Array;
  private readonly positionAttr: THREE.BufferAttribute;

  constructor(weather: Weather, opts: RainOptions = {}) {
    this.weather = weather;
    this.area = opts.area ?? 40;
    this.maxDrops = opts.maxDrops ?? 1500;

    this.group = new THREE.Group();
    this.group.name = 'Rain';

    const n = this.maxDrops;
    this.positions = new Float32Array(n * 3);
    this.velocities = new Float32Array(n * 3);

    for (let i = 0; i < n; i++) {
      const i3 = i * 3;
      this.positions[i3] = (Math.random() * 2 - 1) * this.area;
      this.positions[i3 + 1] = Math.random() * 14;
      this.positions[i3 + 2] = (Math.random() * 2 - 1) * this.area;

      // Slight wind-skew on X/Z keeps drops from looking like a uniform stream.
      this.velocities[i3] = (Math.random() - 0.5) * 0.6;
      this.velocities[i3 + 1] = -12 + (Math.random() - 0.5) * 1.5;
      this.velocities[i3 + 2] = (Math.random() - 0.5) * 0.6;
    }

    this.geometry = new THREE.BufferGeometry();
    this.positionAttr = new THREE.BufferAttribute(this.positions, 3);
    this.positionAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('position', this.positionAttr);
    this.geometry.setAttribute('velocity', new THREE.BufferAttribute(this.velocities, 3));
    this.geometry.setDrawRange(0, 0);

    this.material = new THREE.PointsMaterial({
      color: 0xbcd4e0,
      size: 0.04,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.group.add(this.points);
  }

  update(dt: number, camera: THREE.Camera): void {
    const intensity = Math.max(0, Math.min(1, this.weather.state.rain));
    const drops = Math.floor(intensity * this.maxDrops);
    this.geometry.setDrawRange(0, drops);

    if (drops === 0) {
      return;
    }

    const cx = camera.position.x;
    const cz = camera.position.z;
    const area = this.area;
    const pos = this.positions;
    const vel = this.velocities;

    for (let i = 0; i < drops; i++) {
      const i3 = i * 3;
      pos[i3] += vel[i3] * dt;
      pos[i3 + 1] += vel[i3 + 1] * dt;
      pos[i3 + 2] += vel[i3 + 2] * dt;

      // Keep active particles within the area box around the camera XZ —
      // wrap on X/Z so moving the camera doesn't strand drops behind.
      let dx = pos[i3] - cx;
      if (dx > area) dx -= area * 2;
      else if (dx < -area) dx += area * 2;
      pos[i3] = cx + dx;

      let dz = pos[i3 + 2] - cz;
      if (dz > area) dz -= area * 2;
      else if (dz < -area) dz += area * 2;
      pos[i3 + 2] = cz + dz;

      if (pos[i3 + 1] < 0) {
        pos[i3] = cx + (Math.random() * 2 - 1) * area;
        pos[i3 + 1] = 8 + Math.random() * 6;
        pos[i3 + 2] = cz + (Math.random() * 2 - 1) * area;
      }
    }

    this.positionAttr.needsUpdate = true;
    this.positionAttr.addUpdateRange(0, drops * 3);
  }
}
